/* ============================================================
   src/components/LeadPickerModal.tsx
   ----------------------------------------------------------------
   "Pick a lead" — the one way to fill the Customer fields from
   Zoho (replaces the separate "Import from Zoho" picker).

   Opens on MY LEADS: the saved list (useLeadList), shown at once,
   online or offline; picking one fills from the saved contact with
   no network. On top, online only:
   - a last-name search box (Contacts + Leads, any date);
   - a ‹ date › stepper that switches to ALL APPOINTMENTS for that
     day — everyone's, fetched live (`list`), with "Back to my
     leads". That view lives in this component's state only and is
     never written to the saved list;
   - "Refresh from Zoho": re-downloads the saved list (and the day
     on screen).
   Offline, those controls are greyed out with a note and the saved
   list keeps working. Search and day-view picks look the contact up
   live (`resolve`) — the same mapping the saved list was built with.
   ============================================================ */

import React, { useEffect, useRef, useState } from "react";
import { supabase } from "../supabaseClient";
import {
  formatApptDateLabel,
  formatSearchResultName,
  formatSearchResultPlace,
  nyTodayDate,
  searchResultYear,
  searchZohoByLastName,
  stepApptDate,
  ZOHO_SEARCH_DEBOUNCE_MS,
  ZOHO_SEARCH_MIN_CHARS,
  type ZohoAppt,
  type ZohoSearchResult,
} from "../shared/zohoImport";
import {
  groupLeadsByDay,
  nyTimeOf,
  type LeadContact,
  type OfflineLead,
} from "../shared/leadList";
import type { LeadListState } from "../shared/useLeadList";

export const OFFLINE_NOTE = "Offline — showing saved leads";

type Props = {
  open: boolean;
  onClose: () => void;
  isOnline: boolean;
  /** Zoho access (the allow-list): search, day view and refresh. */
  canUseZoho: boolean;
  leads: LeadListState;
  /** Ask before replacing Customer fields that already hold something.
   *  Returns true to go ahead. */
  confirmReplace: (prompt: string) => boolean;
  /** Fill the Customer fields; `toast` is the confirmation to show. */
  onFill: (contact: Partial<LeadContact>, toast: string) => void;
  /** A live lookup failed (the picker stays open). */
  onError: (message: string) => void;
};

function formatUpdated(ms: number): string {
  const d = new Date(ms);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString([], { weekday: "short", hour: "numeric", minute: "2-digit" });
}

export default function LeadPickerModal({
  open,
  onClose,
  isOnline,
  canUseZoho,
  leads,
  confirmReplace,
  onFill,
  onError,
}: Props) {
  const { leadList, refreshing, refreshFailed, refresh, reload } = leads;
  const zohoOk = isOnline && canUseZoho;

  // "mine" = the saved list; "day" = everyone's appointments on `date`.
  const [view, setView] = useState<"mine" | "day">("mine");
  const [date, setDate] = useState<string>(() => nyTodayDate());
  const [dayAppts, setDayAppts] = useState<ZohoAppt[]>([]);
  const [dayLoading, setDayLoading] = useState(false);
  const [dayError, setDayError] = useState("");
  // A slow day fetch must not land after the user stepped on or went back.
  const daySeq = useRef(0);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<ZohoSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchCapped, setSearchCapped] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const searchSeq = useRef(0);

  const [resolvingId, setResolvingId] = useState<string | null>(null);

  const trimmedQuery = query.trim();
  const searchMode = trimmedQuery.length > 0;

  // Every open starts on my leads, today, empty search — and drops days that
  // ended while the app stayed open.
  useEffect(() => {
    if (!open) return;
    reload();
    daySeq.current += 1;
    searchSeq.current += 1;
    setView("mine");
    setDate(nyTodayDate());
    setDayAppts([]);
    setDayError("");
    setDayLoading(false);
    setQuery("");
    setResolvingId(null);
  }, [open, reload]);

  // Going offline: the live views can't load or resolve, so fall back to the
  // saved list.
  useEffect(() => {
    if (zohoOk) return;
    daySeq.current += 1;
    setDayLoading(false);
    setView("mine");
    setQuery("");
  }, [zohoOk]);

  // Debounced last-name search; the seq guard drops out-of-order replies. A
  // failed search is a soft empty state (searchZohoByLastName never throws).
  useEffect(() => {
    if (!open || !zohoOk || trimmedQuery.length < ZOHO_SEARCH_MIN_CHARS) {
      searchSeq.current += 1;
      setResults([]);
      setSearching(false);
      setSearchCapped(false);
      setSearchFailed(false);
      return;
    }
    const seq = ++searchSeq.current;
    setSearching(true);
    setSearchFailed(false);
    const timer = window.setTimeout(async () => {
      const { items, capped, failed } = await searchZohoByLastName(trimmedQuery);
      if (seq !== searchSeq.current) return;
      setResults(items);
      setSearchCapped(capped);
      setSearchFailed(failed);
      setSearching(false);
    }, ZOHO_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [open, zohoOk, trimmedQuery]);

  if (!open) return null;

  // Everyone's appointments for one NY day — component state only.
  const loadDay = async (day: string) => {
    const seq = ++daySeq.current;
    setDayLoading(true);
    setDayError("");
    setDayAppts([]);
    try {
      const { data, error } = await supabase.functions.invoke("zoho-appointments", {
        body: { action: "list", date: day },
      });
      if (error) throw error;
      if (seq !== daySeq.current) return;
      setDayAppts(Array.isArray((data as any)?.items) ? (data as any).items : []);
    } catch (e) {
      if (seq !== daySeq.current) return;
      console.error("Zoho list failed", e);
      setDayError("Couldn't reach Zoho — try again or enter client info manually");
    } finally {
      if (seq === daySeq.current) setDayLoading(false);
    }
  };

  const showDay = (day: string) => {
    setView("day");
    setDate(day);
    void loadDay(day);
  };

  const busy = !!resolvingId;
  const navDisabled = !zohoOk || busy || searching || (view === "day" && dayLoading);

  // Sundays are skipped both ways (no Sunday meetings). From my leads the
  // stepper starts at today.
  const step = (delta: number) => {
    if (navDisabled) return;
    showDay(stepApptDate(view === "day" ? date : nyTodayDate(), delta));
  };

  const backToMine = () => {
    daySeq.current += 1;
    setDayLoading(false);
    setView("mine");
  };

  const refreshFromZoho = () => {
    if (!zohoOk) return;
    void refresh();
    if (view === "day") void loadDay(date);
  };

  const close = () => {
    if (!busy) onClose();
  };

  // Search hits and day-view appointments: look the contact up live.
  const resolveAndFill = async (
    id: string,
    module: string,
    rowKey: string,
    prompt: string
  ) => {
    if (busy || !confirmReplace(prompt)) return;
    setResolvingId(rowKey);
    try {
      const { data, error } = await supabase.functions.invoke("zoho-appointments", {
        body: { action: "resolve", id, module },
      });
      if (error) throw error;
      const c = (data as any)?.contact;
      if (!c) throw new Error("No contact returned");
      onFill(c, "Client info imported from Zoho ✅");
      onClose();
    } catch (e) {
      console.error("Zoho resolve failed", e);
      onError("Couldn't reach Zoho — enter client info manually");
    } finally {
      setResolvingId(null);
    }
  };

  const pickSaved = (lead: OfflineLead) => {
    if (!lead.contact) return;
    if (!confirmReplace("The Customer fields already have info. Replace them with this lead?")) {
      return;
    }
    onFill(lead.contact, "Client info filled from your lead list ✅");
    onClose();
  };

  const today = nyTodayDate();

  const savedList = !leadList ? (
    <div className="zoho-appt-empty">
      {refreshing ? (
        <>
          <span className="zoho-spinner" aria-hidden="true" />
          Downloading your appointments…
        </>
      ) : isOnline ? (
        "Couldn't download your appointments yet — try again in a moment."
      ) : (
        "No appointments saved on this computer yet. Connect once to download them."
      )}
    </div>
  ) : leadList.leads.length === 0 ? (
    <div className="zoho-appt-empty">
      No appointments with a linked contact from today through next week.
    </div>
  ) : (
    groupLeadsByDay(leadList.leads).map(({ day, leads: dayLeads }) => (
      <React.Fragment key={day}>
        <div className="lead-day-head">{formatApptDateLabel(day)}</div>
        {dayLeads.map((lead) => {
          const c = lead.contact;
          const who =
            c?.fullName ||
            [c?.firstName, c?.lastName].filter(Boolean).join(" ") ||
            lead.contactName;
          const town = c?.city || "";
          return (
            <button
              key={lead.eventId}
              type="button"
              className="zoho-appt-row"
              disabled={!c}
              onClick={() => pickSaved(lead)}
              title={c ? undefined : "Zoho has no contact details for this appointment"}
            >
              <span className="zoho-appt-time">{nyTimeOf(lead.startTime) || "—"}</span>
              <span className="lead-row-main">
                <span className="zoho-appt-title">{lead.title}</span>
                <span className="lead-row-contact">
                  {c
                    ? [who, town].filter(Boolean).join(" · ")
                    : `${lead.contactName || "Contact"} — no contact details`}
                </span>
              </span>
            </button>
          );
        })}
      </React.Fragment>
    ))
  );

  const dayList = dayLoading ? (
    <div className="zoho-appt-empty">
      <span className="zoho-spinner" aria-hidden="true" />
      Loading appointments…
    </div>
  ) : dayError ? (
    <div className="zoho-appt-empty">{dayError}</div>
  ) : dayAppts.length === 0 ? (
    <div className="zoho-appt-empty">
      No appointments with a linked contact on {formatApptDateLabel(date)}
    </div>
  ) : (
    dayAppts.map((appt) => {
      const rowKey = `evt:${appt.eventId}`;
      return (
        <button
          key={appt.eventId}
          type="button"
          className="zoho-appt-row"
          disabled={busy}
          onClick={() =>
            resolveAndFill(
              appt.contactId,
              appt.contactModule,
              rowKey,
              "The Customer fields already have info. Replace them with this appointment's contact?"
            )
          }
        >
          <span className="zoho-appt-time">{nyTimeOf(appt.startTime) || "—"}</span>
          <span className="zoho-appt-title">{appt.title}</span>
          {resolvingId === rowKey && <span className="zoho-spinner" aria-hidden="true" />}
        </button>
      );
    })
  );

  const searchList =
    trimmedQuery.length < ZOHO_SEARCH_MIN_CHARS ? (
      <div className="zoho-appt-empty">
        Type at least {ZOHO_SEARCH_MIN_CHARS} letters of a last name
      </div>
    ) : searching ? (
      <div className="zoho-appt-empty">
        <span className="zoho-spinner" aria-hidden="true" />
        Searching Zoho…
      </div>
    ) : searchFailed ? (
      <div className="zoho-appt-empty">
        Couldn't reach Zoho — try again or enter client info manually
      </div>
    ) : results.length === 0 ? (
      <div className="zoho-appt-empty">
        No customers found matching “{trimmedQuery}” — search matches last names only
      </div>
    ) : (
      results.map((r) => {
        const rowKey = `res:${r.id}`;
        const place = formatSearchResultPlace(r);
        const year = searchResultYear(r.createdTime);
        return (
          <button
            key={rowKey}
            type="button"
            className="zoho-appt-row"
            disabled={busy}
            onClick={() =>
              resolveAndFill(
                r.id,
                r.module,
                rowKey,
                "The Customer fields already have info. Replace them with this customer?"
              )
            }
          >
            <span className="zoho-result-name">{formatSearchResultName(r)}</span>
            <span className="zoho-result-place">{place || "—"}</span>
            {year && <span className="zoho-result-year">{year}</span>}
            {resolvingId === rowKey && <span className="zoho-spinner" aria-hidden="true" />}
          </button>
        );
      })
    );

  return (
    <div
      className="du-modal-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className="du-modal-card" role="dialog" aria-modal="true" aria-label="Pick a lead">
        <div className="du-modal-title">Pick a lead</div>

        <div className="zoho-search">
          <span className="zoho-search-icon" aria-hidden="true">
            ⌕
          </span>
          <input
            type="text"
            className="zoho-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search Zoho by last name…"
            disabled={!zohoOk || busy}
            aria-label="Search Zoho customers by last name"
            autoComplete="off"
            spellCheck={false}
          />
          {query !== "" && (
            <button
              type="button"
              className="zoho-search-clear"
              onClick={() => setQuery("")}
              disabled={busy}
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>

        {!searchMode && (
          <>
            <div className="zoho-date-nav">
              <button
                type="button"
                className="zoho-date-arrow"
                onClick={() => step(-1)}
                disabled={navDisabled}
                aria-label="Previous day — all appointments"
              >
                ‹
              </button>
              {view === "mine" ? (
                <button
                  type="button"
                  className="zoho-date-label lead-date-today"
                  onClick={() => showDay(today)}
                  disabled={navDisabled}
                  title="Show everyone's appointments today"
                >
                  {formatApptDateLabel(today)}
                </button>
              ) : (
                <span className="zoho-date-label">{formatApptDateLabel(date)}</span>
              )}
              <button
                type="button"
                className="zoho-date-arrow"
                onClick={() => step(1)}
                disabled={navDisabled}
                aria-label="Next day — all appointments"
              >
                ›
              </button>
            </div>

            <div className="lead-view-bar">
              <span className="lead-view-name">
                {view === "mine" ? "My leads · today through next week" : "All appointments"}
              </span>
              {view === "day" ? (
                <button
                  type="button"
                  className="lead-link"
                  onClick={backToMine}
                  disabled={busy}
                >
                  Back to my leads
                </button>
              ) : null}
              {canUseZoho && (
                <button
                  type="button"
                  className="lead-link"
                  onClick={refreshFromZoho}
                  disabled={!zohoOk || refreshing || busy}
                >
                  {refreshing ? "Refreshing…" : "Refresh from Zoho"}
                </button>
              )}
            </div>
          </>
        )}

        {!isOnline && <div className="lead-offline-note">{OFFLINE_NOTE}</div>}

        <div className="zoho-appt-list">
          {searchMode ? searchList : view === "day" ? dayList : savedList}
        </div>

        {searchMode && !searching && results.length > 0 && (
          <div className="zoho-search-note">
            {searchCapped
              ? `Showing the ${results.length} newest — type more letters to narrow.`
              : `${results.length} ${results.length === 1 ? "match" : "matches"} for “${trimmedQuery}”, newest first.`}
          </div>
        )}
        {!searchMode && view === "mine" && leadList && (
          <div className="zoho-search-note">
            {refreshing
              ? "Updating…"
              : `Last updated ${formatUpdated(leadList.fetchedAt)}${
                  refreshFailed && isOnline ? " — couldn't refresh, showing the saved list" : ""
                }`}
          </div>
        )}

        <div className="du-modal-actions">
          <span />
          <div className="du-modal-actions-right">
            <button
              type="button"
              className="du-btn du-btn-ghost"
              onClick={close}
              disabled={busy}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
